import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X, Bed, UtensilsCrossed } from "lucide-react";
import { getUploadUrl } from "@/lib/utils";
import { getTranslatedValue } from "@/components/TranslatableField";
import { quoteNickname } from "@/lib/utils";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation as SwiperNav, Pagination as SwiperPag, Keyboard, Zoom, EffectCoverflow } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/zoom";
import "swiper/css/effect-coverflow";

export function TripLightboxes({
  // Photo lightbox
  lightboxOpen, setLightboxOpen, allPhotos, currentPhotoIndex, setCurrentPhotoIndex,
  // Moto lightbox
  motoLightboxOpen, setMotoLightboxOpen, trip, currentMotoPhotoIndex, setCurrentMotoPhotoIndex, currentLang,
  // Estada lightbox
  alojamentoLightboxOpen, setAlojamentoLightboxOpen, currentAlojamentoPhotos, currentAlojamentoPhotoIndex, currentAlojamentoName, prevAlojamentoPhoto, nextAlojamentoPhoto,
  // Food lightbox
  foodLightboxOpen, setFoodLightboxOpen, currentFoodPhotos, currentFoodPhotoIndex, currentFoodName, prevFoodPhoto, nextFoodPhoto,
}) {
  // Resolve caption: handles both string and translatable object {pt, en, es, fr}
  const rc = (c) => typeof c === "object" && c !== null ? (getTranslatedValue(c, currentLang) || "") : (c || "");
  return (
    <>
      {/* Photo Lightbox — Swiper carousel (b99.10.20) */}
      <style>{`
        .trip-photo-swiper .swiper-button-next,
        .trip-photo-swiper .swiper-button-prev {
          width: 56px;
          height: 56px;
          border-radius: 9999px;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(6px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
          transition: background-color 0.2s, transform 0.2s;
        }
        .trip-photo-swiper .swiper-button-next:hover,
        .trip-photo-swiper .swiper-button-prev:hover {
          background: rgba(0, 0, 0, 0.9);
          transform: scale(1.08);
        }
        .trip-photo-swiper .swiper-button-next::after,
        .trip-photo-swiper .swiper-button-prev::after {
          font-size: 22px;
          font-weight: 900;
        }
      `}</style>
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[100vw] max-h-[100vh] w-screen h-screen p-0 bg-black/95 border-none rounded-none overflow-hidden" hideCloseButton>
          <div className="relative w-full h-full">
            <button onClick={() => setLightboxOpen(false)} className="absolute top-4 right-4 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors z-30" data-testid="lightbox-close">
              <X className="h-6 w-6" />
            </button>
            {allPhotos.length > 0 && (
              <Swiper
                key={lightboxOpen ? "lb-open" : "lb-closed"}
                modules={[SwiperNav, SwiperPag, Keyboard, Zoom, EffectCoverflow]}
                initialSlide={currentPhotoIndex}
                navigation
                pagination={{ type: "fraction" }}
                keyboard={{ enabled: true }}
                zoom={{ maxRatio: 3 }}
                loop={allPhotos.length > 1}
                effect="coverflow"
                grabCursor
                centeredSlides
                slidesPerView="auto"
                coverflowEffect={{
                  rotate: 28,
                  stretch: 0,
                  depth: 180,
                  modifier: 1,
                  slideShadows: true,
                }}
                onSlideChange={(sw) => {
                  if (setCurrentPhotoIndex && sw.realIndex !== currentPhotoIndex) {
                    setCurrentPhotoIndex(sw.realIndex);
                  }
                }}
                className="trip-photo-swiper absolute inset-0 w-full h-full"
                style={{
                  "--swiper-navigation-color": "#fff",
                  "--swiper-pagination-color": "#fff",
                  "--swiper-navigation-size": "32px",
                }}
              >
                {allPhotos.map((p, i) => (
                  <SwiperSlide
                    key={p.filename || i}
                    className="!w-[80%] sm:!w-[70%] md:!w-[60%] !h-[85vh]"
                  >
                    <div className="w-full h-full flex items-center justify-center">
                      <img
                        src={getUploadUrl(p.filename)}
                        alt={rc(p.caption) || `Foto ${i + 1}`}
                        className="block object-contain select-none rounded-lg shadow-2xl"
                        style={{ maxWidth: "100%", maxHeight: "85vh", width: "auto", height: "auto" }}
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
            {/* Caption overlay for the active slide */}
            {allPhotos[currentPhotoIndex] && (allPhotos[currentPhotoIndex].caption || allPhotos[currentPhotoIndex].segmentTitle) && (
              <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-center z-20 pointer-events-none w-full px-4">
                {allPhotos[currentPhotoIndex].caption && (
                  <p className="text-white text-sm mb-2 px-4 py-1 bg-black/60 rounded-xl max-w-md mx-auto inline-block">{rc(allPhotos[currentPhotoIndex].caption)}</p>
                )}
                {allPhotos[currentPhotoIndex].segmentTitle && (
                  <div className="bg-black/60 text-white px-3 py-1 rounded-full text-xs inline-block opacity-90 mt-1">
                    {allPhotos[currentPhotoIndex].segmentTitle}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Motorcycle Photo Lightbox */}
      <Dialog open={motoLightboxOpen} onOpenChange={setMotoLightboxOpen}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 bg-transparent border-none" hideCloseButton>
          <div className="relative flex flex-col items-center justify-center min-h-[60vh]">
            <button onClick={() => setMotoLightboxOpen(false)} className="absolute top-4 right-4 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors z-10" data-testid="moto-lightbox-close"><X className="h-6 w-6" /></button>
            {trip?.motorcycle?.photos?.length > 0 && (
              <>
                <img loading="lazy" src={getUploadUrl(trip.motorcycle.photos[currentMotoPhotoIndex]?.url)} alt={getTranslatedValue(trip.motorcycle.nickname, currentLang)} className="max-w-full max-h-[80vh] object-contain" />
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center">
                  <p className="text-white text-sm mb-2 px-4 py-1 bg-black/60 rounded-xl">
                    {quoteNickname(getTranslatedValue(trip.motorcycle.nickname, currentLang))}
                    {(trip.motorcycle.brand || trip.motorcycle.model) && <span className="opacity-70 ml-2">{[trip.motorcycle.brand, trip.motorcycle.model].filter(Boolean).join(" ")}</span>}
                  </p>
                  {trip.motorcycle.photos.length > 1 && (
                    <div className="bg-black/60 text-white px-3 py-1 rounded-full text-sm inline-block">{currentMotoPhotoIndex + 1} / {trip.motorcycle.photos.length}</div>
                  )}
                </div>
                {trip.motorcycle.photos.length > 1 && (
                  <>
                    <button onClick={() => setCurrentMotoPhotoIndex((p) => p === 0 ? trip.motorcycle.photos.length - 1 : p - 1)} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"><ChevronLeft className="h-6 w-6" /></button>
                    <button onClick={() => setCurrentMotoPhotoIndex((p) => p === trip.motorcycle.photos.length - 1 ? 0 : p + 1)} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"><ChevronRight className="h-6 w-6" /></button>
                  </>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Estada (Accommodation) Photo Lightbox */}
      <Dialog open={alojamentoLightboxOpen} onOpenChange={setAlojamentoLightboxOpen}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 bg-transparent border-none" hideCloseButton>
          <div className="relative flex flex-col items-center justify-center min-h-[60vh]">
            <button onClick={() => setAlojamentoLightboxOpen(false)} className="absolute top-4 right-4 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors z-10" data-testid="alojamento-lightbox-close"><X className="h-6 w-6" /></button>
            {currentAlojamentoPhotos.length > 0 && (
              <>
                <img loading="lazy" src={getUploadUrl(currentAlojamentoPhotos[currentAlojamentoPhotoIndex]?.filename)} alt={rc(currentAlojamentoPhotos[currentAlojamentoPhotoIndex]?.caption) || `Foto ${currentAlojamentoPhotoIndex + 1}`} className="max-w-full max-h-[80vh] object-contain" />
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center">
                  {currentAlojamentoName && <p className="text-white text-sm mb-2 px-4 py-1 bg-amber-600/80 rounded-xl flex items-center gap-2"><Bed className="h-4 w-4" />{currentAlojamentoName}</p>}
                  {currentAlojamentoPhotos[currentAlojamentoPhotoIndex]?.caption && <p className="text-white text-sm mb-2 px-4 py-1 bg-black/60 rounded-xl max-w-md">{rc(currentAlojamentoPhotos[currentAlojamentoPhotoIndex].caption)}</p>}
                  <div className="bg-black/60 text-white px-3 py-1 rounded-full text-sm inline-block">{currentAlojamentoPhotoIndex + 1} / {currentAlojamentoPhotos.length}</div>
                </div>
                {currentAlojamentoPhotos.length > 1 && (
                  <>
                    <button onClick={prevAlojamentoPhoto} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors" data-testid="alojamento-lightbox-prev"><ChevronLeft className="h-6 w-6" /></button>
                    <button onClick={nextAlojamentoPhoto} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors" data-testid="alojamento-lightbox-next"><ChevronRight className="h-6 w-6" /></button>
                  </>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Food Photo Lightbox */}
      <Dialog open={foodLightboxOpen} onOpenChange={setFoodLightboxOpen}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 bg-transparent border-none" hideCloseButton>
          <div className="relative flex flex-col items-center justify-center min-h-[60vh]">
            <button onClick={() => setFoodLightboxOpen(false)} className="absolute top-4 right-4 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors z-10" data-testid="food-lightbox-close"><X className="h-6 w-6" /></button>
            {currentFoodPhotos.length > 0 && (
              <>
                <img loading="lazy" src={getUploadUrl(currentFoodPhotos[currentFoodPhotoIndex]?.filename)} alt={rc(currentFoodPhotos[currentFoodPhotoIndex]?.caption) || `Foto ${currentFoodPhotoIndex + 1}`} className="max-w-full max-h-[80vh] object-contain" />
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center">
                  {currentFoodName && <p className="text-white text-sm mb-2 px-4 py-1 bg-orange-600/80 rounded-xl flex items-center gap-2"><UtensilsCrossed className="h-4 w-4" />{currentFoodName}</p>}
                  {currentFoodPhotos[currentFoodPhotoIndex]?.caption && <p className="text-white text-sm mb-2 px-4 py-1 bg-black/60 rounded-xl max-w-md">{rc(currentFoodPhotos[currentFoodPhotoIndex].caption)}</p>}
                  <div className="bg-black/60 text-white px-3 py-1 rounded-full text-sm inline-block">{currentFoodPhotoIndex + 1} / {currentFoodPhotos.length}</div>
                </div>
                {currentFoodPhotos.length > 1 && (
                  <>
                    <button onClick={prevFoodPhoto} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors" data-testid="food-lightbox-prev"><ChevronLeft className="h-6 w-6" /></button>
                    <button onClick={nextFoodPhoto} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors" data-testid="food-lightbox-next"><ChevronRight className="h-6 w-6" /></button>
                  </>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
